import { Wind, Clock, Percent } from "lucide-react";

const fmtH = (h) => {
  if (h == null) return "—";
  if (h <= 0) return "NOW";
  const d = Math.floor(h / 24);
  const r = Math.round(h % 24);
  return d ? `${d}d ${r}h` : `${r}h`;
};

export default function WindArrivalPanel({ wind, drillHour }) {
  const drill = !!wind && wind.source !== "NHC_OFFICIAL";
  const replay = wind?.source === "HISTORICAL_REPLAY";
  const rel = (p) => (p ? (drill ? p.hour - (drillHour || 0) : p.hours_from_now) : null);
  const pct = wind?.probability?.pct ?? 0;
  const band = wind?.probability?.band ?? "<5%";
  const expected = !!wind?.expected;
  const color = !expected ? "#10B981" : pct >= 50 ? "#FF3B30" : pct >= 20 ? "#FF9F0A" : "#FBBF24";

  return (
    <div data-testid="wind-arrival-panel" className="glass rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wind className="w-4 h-4 text-cyan" />
          <h2 className="font-heading font-semibold uppercase tracking-wide text-sm text-slate-100">Storm-Wind Arrival</h2>
        </div>
        <span data-testid="wind-arrival-source" className="font-mono-t text-[9px] uppercase tracking-widest"
          style={{ color: replay ? "#fcd34d" : drill ? "#f87171" : "#64748b" }}>
          {replay ? "REPLAY · BEST TRACK" : drill ? "SIMULATED" : "NHC TOA · WSP"}
        </span>
      </div>

      {!wind ? (
        <div className="font-mono-t text-[11px] text-slate-500">Loading NHC arrival products…</div>
      ) : !expected ? (
        <div data-testid="wind-arrival-status" className="rounded-md px-3 py-2 border border-emerald-500/40 bg-emerald-500/10">
          <div className="font-heading font-semibold text-sm text-emerald-400 uppercase">34-kt winds not expected</div>
          <div className="font-mono-t text-[10px] text-slate-400 mt-0.5">5-day chance at Barbados: {band}</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Slot testid="wind-earliest" label="Earliest" p={wind.earliest} rel={rel(wind.earliest)} color="#FF9F0A" />
          <Slot testid="wind-most-likely" label="Most likely" p={wind.most_likely} rel={rel(wind.most_likely)} color="#FF3B30" />
        </div>
      )}

      {wind && (
        <div data-testid="wind-probability">
          <div className="flex items-center justify-between font-mono-t text-[9px] uppercase tracking-widest text-slate-500 mb-1">
            <span className="flex items-center gap-1"><Percent className="w-3 h-3" /> Chance of TS-force winds</span>
            <span style={{ color }} className="font-bold">{band}</span>
          </div>
          <div className="h-1.5 rounded bg-slate-800 overflow-hidden">
            <div className="h-full rounded transition-all duration-700" style={{ width: `${Math.max(3, pct)}%`, background: color }} />
          </div>
          {wind.storm && <div className="font-mono-t text-[9px] text-slate-500 mt-1">System: {wind.storm}</div>}
        </div>
      )}
    </div>
  );
}

function Slot({ label, p, rel, color, testid }) {
  return (
    <div data-testid={testid} className="bg-slate-900/60 border border-border rounded-md px-2.5 py-2">
      <div className="flex items-center gap-1 font-mono-t text-[9px] uppercase tracking-widest text-slate-500">
        <Clock className="w-3 h-3" /> {label}
      </div>
      <div className="font-heading font-bold text-lg leading-tight" style={{ color }}>
        {rel != null && rel <= 0 ? "ARRIVED" : `T-${fmtH(rel)}`}
      </div>
      <div className="font-mono-t text-[9px] text-slate-400">{p?.local || "—"}</div>
    </div>
  );
}
